import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const HERO_IMG = "/assets/wrestling-champion-raise.jpg";
const WRESTLING_IMG = "/assets/wrestling-strike.jpg";
const COMEDY_IMG = "/assets/comedy-laughing-ropes.jpg";
const STANDOFF_IMG = "/assets/wrestling-wide-standoff.jpg";

const STEPS = [
  {
    n: "01",
    title: "Tell Us What You're Planning",
    body: "Location, venue, date, audience, wrestling or comedy.",
  },
  {
    n: "02",
    title: "We Discuss The Show",
    body: "What kind of show makes sense for your venue and audience.",
  },
  {
    n: "03",
    title: "We Build The Lineup",
    body: "Hustle Promotions books the appropriate wrestlers or comedians.",
  },
  {
    n: "04",
    title: "We Put On The Show",
    body: "Entertainment and show coordination handled.",
  },
];

const WHY = [
  {
    title: "Experience",
    body: "Built on real experience performing, promoting, and producing professional wrestling and stand-up comedy shows.",
  },
  {
    title: "Talent Network",
    body: "Access to a large network of professional wrestlers and comedians, with lineups built for each client.",
  },
  {
    title: "Flexibility",
    body: "Shows adjusted to venue size, audience, schedule, and desired tone.",
  },
  {
    title: "One Point Of Contact",
    body: "Clients work directly with Hustle Promotions instead of coordinating performers individually.",
  },
  {
    title: "Fully Insured",
    body: "",
  },
];

export default function Home() {
  return (
    <Layout>
      <Seo
        title="Professional Wrestling & Stand-Up Comedy Shows | Hustle Promotions"
        description="Hustle Promotions books professional wrestling and stand-up comedy shows for businesses, venues, fairs, festivals and private events. Professional wrestling available across North Carolina."
      />
      {/* ================= HERO (two-zone: text left, figures right) ================= */}
      <section data-testid="hero-section" className="hero2" id="home-hero">
        <div className="hero2-text">
          <div style={{ maxWidth: "640px" }}>
            <Reveal>
              <p className="eyebrow" data-testid="hero-eyebrow" style={{ marginBottom: "1.75rem" }}>
                Hustle Promotions
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="h-hero" data-testid="hero-headline">
                Professional Wrestling.
                <br />
                Live Comedy.
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p
                className="font-cond"
                data-testid="hero-supporting"
                style={{
                  fontSize: "clamp(1.1rem, 1.9vw, 1.6rem)",
                  textTransform: "uppercase",
                  letterSpacing: "0.01em",
                  fontWeight: 500,
                  margin: "1.75rem 0 1rem",
                  maxWidth: "26ch",
                  lineHeight: 1.15,
                }}
              >
                Two proven ways to give people a reason to show up.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="body-lg" data-testid="hero-subcopy" style={{ maxWidth: "46ch", marginBottom: "2.5rem" }}>
                Hustle Promotions brings professional wrestling and stand-up comedy shows directly to
                businesses, venues, fairs, festivals and private events.
              </p>
            </Reveal>
            <Reveal delay={280}>
              <div className="flex flex-col sm:flex-row sm:flex-wrap" style={{ gap: "1rem" }}>
                <Link to="/book" className="btn btn-solid" data-testid="hero-book-cta">
                  Book A Show <ArrowRight size={16} strokeWidth={2} />
                </Link>
                <a href="#services" className="btn btn-outline" data-testid="hero-explore-cta">
                  Explore Wrestling &amp; Comedy
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="hero2-media">
          <img
            src={HERO_IMG}
            alt="A referee raising a professional wrestler's arm as the champion holds a title belt, black and white"
          />
          <div aria-hidden className="hero2-media-scrim" />
        </div>
      </section>

      {/* ================= TWO-SERVICE SECTION ================= */}
      <section id="services" data-testid="services-section" style={{ background: "var(--black)" }}>
        <div className="container-editorial" style={{ paddingTop: "clamp(4rem, 8vw, 7rem)", paddingBottom: "clamp(1.5rem, 3vw, 2.5rem)" }}>
          <Reveal>
            <p className="eyebrow" style={{ marginBottom: "1rem" }}>What We Do</p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="h-section" data-testid="services-heading" style={{ maxWidth: "18ch" }}>
              Two Services. Done Right.
            </h2>
          </Reveal>
        </div>

        <div className="hairline" />

        <div className="services-split" data-testid="services-split">
          {/* WRESTLING */}
          <article className="service-half" data-testid="service-wrestling">
            <div className="service-media">
              <img
                src={WRESTLING_IMG}
                alt="Two professional wrestlers mid-strike in the ring"
                className="img-cover obj-center-top"
                style={{ height: "100%", width: "100%" }}
              />
              <div aria-hidden className="service-media-overlay" />
              <h3 className="h-display-comedy service-media-title">
                Professional
                <br />
                Wrestling.
              </h3>
            </div>
            <div className="service-body">
              <Reveal>
                <p className="eyebrow" style={{ marginBottom: "1.25rem" }}>North Carolina</p>
                <p className="body-lg" style={{ marginTop: 0, marginBottom: "2rem", color: "rgba(255,255,255,0.78)" }}>
                  Professional wrestling shows for businesses, fairs, festivals, venues and private
                  events throughout North Carolina.
                </p>
                <Link to="/wrestling" className="link-underline" data-testid="service-wrestling-cta">
                  Explore Wrestling <ArrowRight className="arrow" size={16} strokeWidth={2} />
                </Link>
              </Reveal>
            </div>
          </article>

          {/* COMEDY — typographic, no fake imagery */}
          <article className="service-half service-half-divider" data-testid="service-comedy">
            <div className="service-media" data-testid="comedy-image-slot">
              <img
                src={COMEDY_IMG}
                alt="A comedian laughing while leaning on the ring ropes during a live stand-up set"
                className="img-cover obj-right-bottom"
                style={{ height: "100%", width: "100%" }}
              />
              <div aria-hidden className="service-media-overlay" />
              <h3 className="h-display-comedy service-media-title">
                Live
                <br />
                Comedy.
              </h3>
            </div>
            <div className="service-body">
              <Reveal>
                <p className="eyebrow" style={{ marginBottom: "1.25rem" }}>Stand-Up</p>
                <p className="body-lg" style={{ marginTop: 0, marginBottom: "2rem", color: "rgba(255,255,255,0.78)" }}>
                  Professional stand-up comedy shows for businesses, restaurants, breweries, venues
                  and private events.
                </p>
                <Link to="/comedy" className="link-underline" data-testid="service-comedy-cta">
                  Explore Comedy <ArrowRight className="arrow" size={16} strokeWidth={2} />
                </Link>
              </Reveal>
            </div>
          </article>
        </div>
      </section>

      {/* ================= WHY HUSTLE PROMOTIONS ================= */}
      <section data-testid="why-hustle-section" style={{ background: "var(--black)" }}>
        <div className="hairline" />
        <div className="container-editorial section-pad">
          <div className="split-hero" style={{ alignItems: "start" }}>
            <div>
              <Reveal>
                <p className="eyebrow" style={{ marginBottom: "1rem" }}>Why Hustle Promotions</p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="h-section" style={{ maxWidth: "12ch" }}>
                  Built To Deliver.
                </h2>
              </Reveal>
            </div>
            <div className="why-list" data-testid="why-hustle-list">
              {WHY.map((w, i) => (
                <Reveal key={w.title} delay={i * 70}>
                  <div className="why-row" data-testid={`why-point-${i + 1}`}>
                    <span className="why-num">0{i + 1}</span>
                    <div>
                      <h3 className="why-title">{w.title}</h3>
                      {w.body && (
                        <p className="body-md" style={{ margin: 0, maxWidth: "48ch" }}>
                          {w.body}
                        </p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= CINEMATIC PHOTO BREAK ================= */}
      <section data-testid="photo-break-section" className="relative" style={{ background: "var(--black)" }}>
        <div className="photo-break-wrap">
          <img
            src={STANDOFF_IMG}
            alt="Two wrestlers square off in the ring beneath an exit sign"
            className="img-cover obj-center-bottom"
            style={{ position: "absolute", inset: 0, height: "100%", width: "100%" }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.25) 42%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0.6) 100%)",
            }}
          />
          <div
            className="container-editorial relative"
            style={{ height: "100%", display: "flex", alignItems: "flex-end", paddingBottom: "clamp(2rem, 5vw, 4rem)", paddingTop: "clamp(2rem, 5vw, 4rem)" }}
          >
            <Reveal>
              <h2 className="h-section" data-testid="photo-break-headline" style={{ maxWidth: "20ch" }}>
                Professional Wrestling
                <br />
                Across North Carolina.
              </h2>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= BOOKING PROCESS ================= */}
      <section data-testid="booking-process-section" style={{ background: "var(--off-black)" }}>
        <div className="hairline" />
        <div className="container-editorial" style={{ paddingTop: "clamp(4rem, 8vw, 7rem)", paddingBottom: "clamp(4rem, 8vw, 7rem)" }}>
          <div className="grid" style={{ gridTemplateColumns: "1fr", gap: "clamp(2.5rem, 5vw, 4rem)" }}>
            <div className="booking-header">
              <Reveal>
                <p className="eyebrow" style={{ marginBottom: "1rem" }}>How It Works</p>
                <h2 className="h-section" style={{ maxWidth: "16ch" }}>
                  The Booking Process
                </h2>
              </Reveal>
            </div>

            <div className="steps-grid" data-testid="booking-steps">
              {STEPS.map((step, i) => (
                <Reveal key={step.n} delay={i * 90}>
                  <div className="step-item" data-testid={`booking-step-${i + 1}`}>
                    <span className="step-number">{step.n}</span>
                    <div className="step-hairline" />
                    <h3 className="h-step" style={{ marginTop: "1.25rem", marginBottom: "0.75rem" }}>
                      {step.title}
                    </h3>
                    <p className="body-md" style={{ margin: 0 }}>
                      {step.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="hairline" />
        {/* Closing CTA band */}
        <div className="container-editorial" style={{ paddingTop: "clamp(3.5rem, 6vw, 5.5rem)", paddingBottom: "clamp(3.5rem, 6vw, 5.5rem)" }}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between" style={{ gap: "2rem" }}>
            <Reveal>
              <h2 className="h-section" style={{ maxWidth: "16ch" }}>
                Ready To Put On A Show?
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <Link to="/book" className="btn btn-solid" data-testid="closing-book-cta">
                Book A Show <ArrowRight size={16} strokeWidth={2} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <style>{`
        .photo-break-wrap {
          position: relative;
          height: clamp(360px, 58vh, 640px);
          overflow: hidden;
          border-top: 1px solid var(--hairline-dark);
          border-bottom: 1px solid var(--hairline-dark);
        }
        .services-split { display: grid; grid-template-columns: 1fr; }
        .service-half { display: flex; flex-direction: column; }
        .service-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #050505;
          overflow: hidden;
        }
        .service-media-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.85) 100%);
        }
        .service-media-title {
          position: absolute;
          left: clamp(1.25rem, 3vw, 2.5rem);
          bottom: clamp(1.25rem, 3vw, 2.25rem);
          color: var(--white);
          margin: 0;
        }
        .service-body {
          padding: clamp(2rem, 3.5vw, 3rem) clamp(1.25rem, 3vw, 2.5rem) clamp(3rem, 5vw, 4rem);
          max-width: 46ch;
        }
        .service-half-divider { border-top: 1px solid var(--hairline-dark); }

        .steps-grid { display: grid; grid-template-columns: 1fr; gap: 0; }
        .step-item { padding: clamp(1.75rem, 3vw, 2.25rem) 0; border-top: 1px solid var(--hairline-dark); }
        .step-item:first-child { border-top: 0; }
        .step-hairline { display: none; }

        @media (min-width: 768px) {
          .services-split { grid-template-columns: 1fr 1fr; }
          .service-half-divider { border-top: 0; border-left: 1px solid var(--hairline-dark); }
          .service-media { aspect-ratio: 3 / 4; }
          .steps-grid { grid-template-columns: repeat(2, 1fr); gap: 1px; background: var(--hairline-dark); }
          .step-item {
            background: var(--off-black);
            border-top: 0;
            padding: clamp(2rem, 3vw, 2.75rem) clamp(1.5rem, 2.5vw, 2.25rem);
          }
        }
        @media (min-width: 1100px) {
          .steps-grid { grid-template-columns: repeat(4, 1fr); }
        }
      `}</style>
    </Layout>
  );
}
